import {EditOutlined} from '@ant-design/icons';
import {LoadingOutlined} from '@ant-design/icons/lib';
import {Button, Card, Progress, Typography} from 'antd';
import {RiskProfile, UpdateAccountInput} from 'api/generated';
import {AnchorLink, PropertyList, Stack} from 'components';
import {Formik} from 'formik';
import {Form, FormItem, Input, Select, SubmitButton} from 'formik-antd';
import humanize from 'humanize-string';
import {formatDate, formatHumanString} from 'lib/formaters';
import {clamp} from 'lib/utils';
import React, {useState} from 'react';
import {SectionProps} from 'routes/Account/types';
import styled from 'styled-components';
import * as yup from 'yup';
import {useAccessLevel} from '../../hooks/useAccessLevel';
import {useIgnoredKeywords} from '../../hooks/useIgnoredKeywords';

const {Text} = Typography;
const LabelRisk = styled.span`
  display: inline-block;
  width: 30px;
  margin-right: 10px;
`;
const {TextArea} = Input;

export const RiskAssessment: React.FC<
  Pick<SectionProps, 'account' | 'cardProps' | 'formProps' | 'onSubmit'>
> = ({cardProps, formProps, account, onSubmit}) => {
  const [isEditing, setEditing] = useState(false);

  const {keywordsOptions} = useIgnoredKeywords(account!);

  const {isAdmin} = useAccessLevel();

  if (!account) return null;

  const riskLabels = account?.riskLabels?.slice() || [];
  riskLabels.sort((a, b) => b.riskScore! - a.riskScore!);
  const riskScore = clamp(account?.riskScore || 0, 0, 100);

  if (account?.riskProcessing) {
    return (
      <Card {...cardProps} title="KYB Risk Assessment">
        <Text type="secondary">
          <LoadingOutlined style={{marginRight: 8}} /> Risk assessment in progress
        </Text>
      </Card>
    );
  }

  if (!account?.riskProfile) return null;

  const initialValues: UpdateAccountInput = {
    id: account?.id,
    riskProfile: account?.riskProfile,
    riskProfileNote: account?.riskProfileNote,
    riskIgnoredKeywords: account?.riskIgnoredKeywords || []
  };

  const validationSchema = yup.object().shape({
    riskProfile: yup.string().required('Required'),
    riskProfileNote: yup.string().nullable(),
    riskIgnoredKeywords: yup.array().of(yup.string())
  });

  const handleSubmit = async (values: UpdateAccountInput) => {
    await onSubmit(values);
    setEditing(false);
  };

  return (
    <Card
      {...cardProps}
      title="KYB Risk Assessment"
      extra={
        isEditing || !isAdmin ? null : (
          <AnchorLink onClick={() => setEditing(true)}>
            <EditOutlined /> Edit data
          </AnchorLink>
        )
      }>
      {isEditing ? (
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}>
          <Form {...formProps}>
            <FormItem name="riskProfile" label="Risk profile">
              <Select name="riskProfile" placeholder="Select option" allowClear>
                {Object.values(RiskProfile).map((riskProfile) => (
                  <Select.Option value={riskProfile}>{humanize(riskProfile)}</Select.Option>
                ))}
              </Select>
            </FormItem>
            <FormItem name="riskProfileNote" label="Risk profile note">
              <TextArea name="riskProfileNote" placeholder="Write a note" />
            </FormItem>
            <FormItem name="riskIgnoredKeywords" label="Ignored keywords">
              <Select
                name="riskIgnoredKeywords"
                mode="multiple"
                allowClear
                placeholder="Select keywords"
                options={keywordsOptions}></Select>
            </FormItem>
            <FormItem
              wrapperCol={{offset: formProps.labelCol?.span, ...formProps.wrapperCol}}
              name="submit">
              <Stack>
                <Button onClick={() => setEditing(false)}>Cancel</Button>
                <SubmitButton>Submit</SubmitButton>
              </Stack>
            </FormItem>
          </Form>
        </Formik>
      ) : (
        <>
          <PropertyList
            omitEmpty
            properties={[
              {
                label: 'Risk profile',
                value: formatHumanString(account?.riskProfile)
              },
              {
                label: 'Risk profile updated',
                value: formatDate(account?.riskUpdatedAt)
              },
              {
                label: 'Risk profile note',
                value: account?.riskProfileNote
              },
              {
                label: 'Ignored keywords',
                value:
                  account.riskIgnoredKeywords && account.riskIgnoredKeywords?.length > 0
                    ? account.riskIgnoredKeywords.join(', ')
                    : null
              },
              {
                label: 'Risk score',
                value: (
                  <Progress
                    percent={riskScore}
                    steps={20}
                    format={(percent = 0) => (
                      <Text
                        type={percent < 15 ? 'success' : percent < 25 ? 'warning' : 'danger'}
                        strong>
                        {percent}
                      </Text>
                    )}
                    strokeColor={Array.from({length: 20}, (_, i) => {
                      if (i * 5 < 15) return '#52c41a';
                      if (i * 5 < 25) return '#faad14';
                      return '#ff4d4f';
                    })}
                    showInfo
                  />
                )
              }
            ]}
          />
          {riskLabels.length > 0 && (
            <PropertyList
              omitEmpty
              style={{marginTop: 15}}
              properties={riskLabels.map((item) => ({
                label: formatHumanString(item.category),
                value:
                  item.riskScore! >= 0 ? (
                    <Text type="danger" strong>
                      <LabelRisk>+{item.riskScore}</LabelRisk> {item.label}
                    </Text>
                  ) : (
                    <Text type="success" strong>
                      <LabelRisk>{item.riskScore}</LabelRisk> {item.label}
                    </Text>
                  )
              }))}
            />
          )}
        </>
      )}
    </Card>
  );
};
